import { useEffect, useRef, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ChevronDown, Download, Search } from "lucide-react";
import {
  searchInstruments,
  type MasterInstrumentOut,
  type StoredInstrumentOut,
} from "../api/instruments";
import { useDebouncedValue } from "../lib/useDebouncedValue";
import { formatInt } from "../lib/format";

/**
 * Server-backed symbol typeahead.
 *
 * The previous version took every instrument as a prop and filtered in JS.
 * With 15,720 instruments that meant a 4.28 MB download and ~78,000 DOM nodes
 * mounted synchronously on open — a multi-second freeze — plus a full re-render
 * on every keystroke and every mouse move.
 *
 * Now the server does the matching and returns at most 10 per section, so there
 * is nothing to virtualize and no reason for a windowing dependency.
 *
 * Query syntax is space-separated AND tokens, ordered base > futures > options,
 * then by expiry (nearest upcoming first, then most recent past):
 *   NIFT         -> NIFTY 50, NIFTY BANK, then futures
 *   NIF FUT      -> NIFTY futures, nearest expiry first
 *   NIF FUT MAR  -> March NIFTY futures only
 */
/** A pick, normalised across both halves of the search.
 *
 *  `stored` says whether it has bars — a backtest needs history and so only
 *  accepts stored, while a deployment trades forward and does not care. */
export interface PickedInstrument {
  id: string;
  display: string;
  symbol: string;
  exchange: string;
  instrument_type: string;
  expiry: string | null;
  strike: string | null;
  lot_size: number;
  tick_size: string;
  stored: boolean;
  timeframes: string[];
}

function fromStored(i: StoredInstrumentOut): PickedInstrument {
  return { ...i, stored: true };
}

function fromMaster(i: MasterInstrumentOut): PickedInstrument {
  return { ...i, stored: false, timeframes: [] };
}

export default function SymbolSearch({
  value,
  onChange,
  onRequestDownload,
  mode = "stored",
}: {
  value: { id: string; display: string } | null;
  onChange: (inst: PickedInstrument) => void;
  onRequestDownload?: (inst: MasterInstrumentOut) => void;
  /** `"stored"` — only symbols with bars are selectable; a not-downloaded row
   *  offers Download instead. `"any"` — anything is selectable, for callers
   *  that do not need history (a deployment trades forward). */
  mode?: "stored" | "any";
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  // Key the query on the DEBOUNCED term, never the raw one — otherwise the
  // cache accumulates an entry per keystroke.
  const debounced = useDebouncedValue(query.trim(), 200);

  const results = useQuery({
    queryKey: ["instrument-search", debounced],
    queryFn: () => searchInstruments(debounced, 10),
    enabled: open, // no network cost while the dropdown is shut
    staleTime: 60_000,
    // Keep the previous list on screen while the next loads: no flicker, no
    // layout jump, and no "no symbols match" flashing mid-word.
    placeholderData: keepPreviousData,
  });

  // Downloading a symbol from search is open to every user: the backend
  // fetches it through the user's OWN broker connection and explains when
  // they have none. Only the Data page (bulk backfills) is admin-granted.
  const canDownload = !!onRequestDownload;

  const stored = results.data?.stored ?? [];
  const master = results.data?.master ?? [];
  // Flattened so ↑/↓ crosses the section boundary naturally.
  const flat: Array<
    { kind: "stored"; item: StoredInstrumentOut } | { kind: "master"; item: MasterInstrumentOut }
  > = [
    ...stored.map((item) => ({ kind: "stored" as const, item })),
    ...master.map((item) => ({ kind: "master" as const, item })),
  ];

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [open]);

  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-idx="${highlight}"]`);
    el?.scrollIntoView({ block: "nearest" });
  }, [highlight, open]);

  useEffect(() => setHighlight(0), [debounced]);
  // Why a click was refused, when it was. Cleared on the next keystroke.
  const [denied, setDenied] = useState<string | null>(null);
  useEffect(() => setDenied(null), [debounced]);

  function choose(idx: number) {
    const row = flat[idx];
    if (!row) return;
    if (row.kind === "stored") {
      onChange(fromStored(row.item));
      setOpen(false);
      setQuery("");
    } else if (mode === "any") {
      // No history needed — take it as-is.
      onChange(fromMaster(row.item));
      setOpen(false);
      setQuery("");
    } else if (canDownload) {
      onRequestDownload?.(row.item);
      setOpen(false);
      setQuery("");
    } else {
      // Previously a total no-op: the row looked clickable, the dropdown did
      // not even close, and nothing explained why. Say so instead.
      setDenied(
        "This symbol has no stored history yet. Open it from the Chart page to download it.",
      );
    }
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, flat.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      choose(highlight);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
          setHighlight(0);
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
        className="inline-flex items-center gap-2 bg-surface border border-border rounded px-3 py-1.5 text-sm font-mono hover:bg-surface-2 w-full sm:w-auto sm:min-w-[220px] justify-between"
      >
        <span className={value ? "text-fg" : "text-muted"}>
          {value ? value.display : "Pick a symbol…"}
        </span>
        <ChevronDown size={14} className="text-muted shrink-0" />
      </button>

      {open && (
        <div className="absolute left-0 mt-1 w-[calc(100vw-2rem)] sm:w-[420px] max-w-[420px] bg-surface border border-border rounded-lg shadow-elev z-20 overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
            <Search size={14} className="text-muted shrink-0" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="e.g. NIFT · NIF FUT · NIF FUT MAR"
              className="w-full bg-transparent text-sm focus:outline-none"
            />
          </div>

          <div ref={listRef} className="max-h-96 overflow-y-auto">
            {results.isError && (
              <div className="px-3 py-4 text-sm text-down-600 dark:text-down-400 text-center">
                Search failed. Try again.
              </div>
            )}

            {!results.isError && flat.length === 0 && (
              <div className="px-3 py-4 text-sm text-muted text-center">
                {results.isFetching ? "Searching…" : "Nothing matches all of those words."}
              </div>
            )}

            {stored.length > 0 && (
              <SectionHeader>In your data</SectionHeader>
            )}
            {stored.map((inst, i) => (
              <Row
                key={inst.id}
                idx={i}
                active={i === highlight}
                onHover={setHighlight}
                onClick={() => choose(i)}
                title={inst.display}
                subtitle={inst.timeframes.join(" · ")}
                right={`${formatInt(inst.total_bars)} bars`}
              />
            ))}

            {master.length > 0 && (
              <SectionHeader>
                Not downloaded yet
                <span className="font-normal text-muted">
                  {" "}
                  — available from your broker
                </span>
              </SectionHeader>
            )}
            {master.map((inst, i) => {
              const idx = stored.length + i;
              return (
                <Row
                  key={inst.id}
                  idx={idx}
                  active={idx === highlight}
                  muted
                  onHover={setHighlight}
                  onClick={() => choose(idx)}
                  title={inst.display}
                  subtitle={inst.name ?? inst.instrument_type}
                  right={
                    canDownload ? (
                      <span className="inline-flex items-center gap-1 text-brand-600 dark:text-brand-400">
                        <Download size={12} /> Download
                      </span>
                    ) : (
                      <span title="Open it from the Chart page to download it">
                        unavailable
                      </span>
                    )
                  }
                />
              );
            })}

            {(results.data?.stored_truncated || results.data?.master_truncated) && (
              <div className="px-3 py-2 text-[11px] text-muted border-t border-border">
                More matches — add another word to narrow it down.
              </div>
            )}

            {denied && (
              <div className="px-3 py-2 text-[11px] text-down-700 dark:text-down-400 bg-down-50 dark:bg-down-500/10 border-t border-border">
                {denied}
              </div>
            )}

            {results.data && results.data.master_snapshot_date === null && (
              <div className="px-3 py-2 text-[11px] text-muted border-t border-border">
                No broker instrument list has been downloaded, so only symbols
                you already have data for can be searched. Snapshot instruments
                from the Brokers page to search everything.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="sticky top-0 bg-surface-2 px-3 py-1 text-[10px] uppercase tracking-wide text-muted font-medium border-b border-border">
      {children}
    </div>
  );
}

function Row({
  idx,
  active,
  muted,
  onHover,
  onClick,
  title,
  subtitle,
  right,
}: {
  idx: number;
  active: boolean;
  muted?: boolean;
  onHover: (idx: number) => void;
  onClick: () => void;
  title: string;
  subtitle: string;
  right: React.ReactNode;
}) {
  return (
    <button
      type="button"
      data-idx={idx}
      onClick={onClick}
      // Bail out when the index is unchanged: React skips the re-render
      // entirely, so moving the mouse within one row costs nothing.
      onMouseEnter={() => onHover(idx)}
      className={`w-full text-left px-3 py-2 flex items-center justify-between gap-2 ${
        active ? "bg-surface-2" : ""
      }`}
    >
      <div className="min-w-0">
        <div
          className={`text-sm font-mono truncate ${muted ? "text-fg-subtle" : "text-fg"}`}
        >
          {title}
        </div>
        <div className="text-[11px] text-muted truncate">{subtitle}</div>
      </div>
      <div className="text-[11px] text-muted font-mono shrink-0">{right}</div>
    </button>
  );
}
